// A question on the phone (Poulpe-624), as the approved prototype drew it
// (mobile/QuestionScreen.tsx), on the real store: its context first, then
// what it asks, with the desktop card's own fields and rule (`Choices`,
// `Words`, `replyOf`, questions/QuestionCard.tsx), and Send in a foot that
// stays. Each state says where the answer is:
//
// - open: answered at the version shown; a revision clears the picks and
//   says so, as the card does; once expired, "Answer anyway";
// - kept: offline, the answer waits in memory while Poulpe stays open, and
//   goes once back online (answers.tsx);
// - sending, then accepted by the server, its delivery to the agent not yet
//   confirmed (Poulpe re-sends it), then delivered;
// - cancelled by its agent, or gone (answered or withdrawn elsewhere).
//
// What the store dropped once it left play (delivered, or cancelled) is read
// from `settled`, which the store keeps while the page runs.
import { useId, useState, type FormEvent, type KeyboardEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import type { Prompt, Question, Reply } from '../api/types'
import { CheckIcon, ChevronRightIcon } from '../components/Icons'
import { Markdown } from '../markdown/Markdown'
import { Choices, NONE, replyOf, replyText, Words, type Pick } from '../questions/QuestionCard'
import { permissionText } from '../questions/permissionText'
import { expired, expiredText, QuestionFlags, QuestionRevised, useExpiryClock, waits } from '../questions/waiting'
import { useBoardState } from '../store/context'
import { senderName } from '../sessions/words'
import { useKeeper, type Kept } from './answers'
import { IssueLink } from './context'
import { askerName, clock, Note, Screen, TopBar } from './shared'

const BACK = <Link className="button m-target" to="/">Back to Needs you</Link>

export function QuestionScreen() {
  const { id = '' } = useParams()
  const { questions, settled, pendingAnswers, me } = useBoardState()
  const { kept } = useKeeper()
  const question = questions.get(id)
  const left = settled.get(id)
  const entry = kept.get(id)
  const top = <TopBar title="Question" />
  const shown = question ?? left
  const mine = (q: Question) => q.target.kind === 'person' && q.target.member === me?.member?.id

  if (!shown) return <Screen top={top} foot={BACK}><Note>This question is no longer here: it was answered or withdrawn elsewhere.</Note></Screen>
  if (!mine(shown)) return <Screen top={top} foot={BACK}><Note>This question is put to someone else.</Note></Screen>
  if (entry?.phase === 'kept') return <Settled question={shown} replies={entry.replies} status={KEPT} />
  if (shown.state === 'cancelled') return <Cancelled question={shown} />
  if (shown.state === 'answered') {
    const answer = shown.answer
    const byMe = answer?.by.kind === 'person' && answer.by.member === me?.member?.id
    const who = answer ? senderName(answer.by, me) : 'Someone'
    // Left play answered: its delivery was confirmed (store.ts, `settled`).
    const delivered = !question || shown.delivery === 'confirmed'
    const status: Status = pendingAnswers.has(shown.id) ? SAVED_HERE
      : !byMe ? { tone: delivered ? 'm-ok' : 'question-changed', text: `${who} answered it${delivered ? ', and the agent has the answer' : ''}. There is nothing more to send.` }
        : delivered ? DELIVERED : shown.delivery === 'uncertain' ? UNCERTAIN : ACCEPTED
    return <Settled question={shown} replies={answer?.replies ?? []} status={status} />
  }
  if (!question) return <Screen top={top} foot={BACK}><Note>This question is no longer here: it was answered or withdrawn elsewhere.</Note></Screen>
  if (question.state !== 'open') return <Cancelled question={question} />
  return <Answering key={question.id} question={question} entry={entry} />
}

type Status = { tone: string; text: string }
const KEPT: Status = { tone: 'question-changed', text: 'Kept on this phone. It goes as soon as the phone is back online, while Poulpe stays open: closing Poulpe before then loses it, and you would answer again.' }
/** An answer this device's store keeps in its outbox, from the desktop layout offline: the server has not taken it yet. */
const SAVED_HERE: Status = { tone: 'question-changed', text: 'Your answer is saved on this device, not yet on the server. It goes when the server is back; you do not need to answer again.' }
const ACCEPTED: Status = { tone: 'question-changed', text: 'Your answer is saved on the server. Poulpe has not yet confirmed that the agent received it, and says so here once it has; you do not need to answer again.' }
const UNCERTAIN: Status = { tone: 'question-changed', text: 'Your answer is saved on the server, but Poulpe has not confirmed that the agent received it. Poulpe sends it again; you do not need to answer again.' }
const DELIVERED: Status = { tone: 'm-ok', text: 'Sent. The agent has your answer.' }

/** The question's context: its project, its issue, who asks, its flags and its version. */
function Context({ question }: { question: Question }) {
  const { issues, projects, sessions } = useBoardState()
  const now = useExpiryClock([question])
  const issue = question.issue ? issues.get(question.issue) : undefined
  const project = issue ? projects.get(issue.project) : undefined
  const session = sessions.get(question.session)
  const here = `/q/${question.id}`
  return (
    <section className="m-context" aria-label="About this question">
      <div className="request-top">
        <span className="kind-chip">Question</span>
        {project && <span className="request-project">{project.name}</span>}
        {issue && <IssueLink issue={issue} from={here} />}
      </div>
      {/* The session that asks: a whole row, a 44 pt target, to its facts (context.tsx); back from there comes here. */}
      <Link className="m-target m-row-link" to={`/sessions/${question.session}?from=${encodeURIComponent(here)}`}>
        <span><span className="muted">Asked by {askerName(session)} in</span>{' '}{session?.title ?? 'its session'}</span>
        <ChevronRightIcon size={14} />
      </Link>
      <p className="request-meta question-terms">
        <QuestionFlags question={question} now={now} />
        {question.expiry && waits(question, now) && <span className="muted">until {clock(question.expiry.at)} · </span>}
        <QuestionRevised question={question} now={now} first={!question.urgent && !question.blocking} />{' '}
        <span className="muted">Version {question.version}</span>
      </p>
    </section>
  )
}

/** A question answered: where its answer is, and what each prompt was answered. */
function Settled({ question, replies, status }: { question: Question; replies: Reply[]; status: Status }) {
  return (
    <Screen top={<TopBar title="Question" />} foot={BACK}>
      <Context question={question} />
      <Note tone={status.tone}>{status.text}</Note>
      <section aria-label="Your answer" className="request-card code-answers">
        {question.prompts.map((prompt, i) => (
          <div key={i} className="m-answer">
            <p className="muted">{permissionText(prompt).title.split('\n')[0]}</p>
            <p className="code-settled-what"><CheckIcon size={12} /> {replyText(prompt, replies[i])}</p>
          </div>
        ))}
      </section>
    </Screen>
  )
}

function Cancelled({ question }: { question: Question }) {
  return (
    <Screen top={<TopBar title="Question" />} foot={BACK}>
      <Context question={question} />
      <Note tone="question-changed">Its agent withdrew this question. There is nothing to answer.</Note>
      <div className="request-card muted">
        {question.prompts.map((prompt, i) => <Asked key={i} text={prompt.text} />)}
      </div>
    </Screen>
  )
}

/** What an agent asks in its own words: the card's Markdown, remote images out, a link showing where it leads. */
function Asked({ text }: { text: string }) {
  return <Markdown source={text} className="question-text" localImagesOnly linkTargets />
}

function promptTitle(prompt: Prompt) {
  return prompt.choices_only ? permissionText(prompt).title : <Asked text={prompt.text} />
}

/** The pick that gives `reply` to its prompt (`replyOf`'s inverse). */
function pickOf(prompt: Prompt, reply: Reply | undefined): Pick {
  if (!reply) return NONE
  if (reply.kind === 'text') return { choices: [], words: reply.value, own: prompt.choices.length > 0 }
  return { choices: reply.kind === 'choice' ? [reply.value] : reply.value, words: '', own: false }
}

/** An open question: the picks for the version shown, and Send. */
function Answering({ question, entry }: { question: Question; entry: Kept | undefined }) {
  const { offline } = useBoardState()
  const { send, draft, keepDraft } = useKeeper()
  const now = useExpiryClock([question])
  const formId = useId()
  const titleId = useId()
  // An answer that failed comes back as it was picked, to try again as it is.
  const [picks, setPicksNow] = useState<readonly Pick[]>(() => draft(question.id, question.version)
    ?? question.prompts.map((prompt, i) => (entry && entry.version === question.version ? pickOf(prompt, entry.replies[i]) : NONE)))
  // Kept as they change, so that the screen comes back with them.
  const setPicks = (next: readonly Pick[] | ((held: readonly Pick[]) => readonly Pick[])) => setPicksNow((held) => {
    const made = typeof next === 'function' ? next(held) : next
    keepDraft(question.id, version, made)
    return made
  })
  const [version, setVersion] = useState(question.version)
  const [changed, setChanged] = useState(false)
  // A new version: what was picked was for the prompts before it.
  if (question.version !== version) {
    setVersion(question.version)
    setPicks(question.prompts.map(() => NONE))
    setChanged(true)
  }
  const { prompts } = question
  const several = prompts.length > 1
  const picked = (i: number) => picks[i] ?? NONE
  const change = (i: number, next: Pick) => setPicks((held) => prompts.map((_, at) => (at === i ? next : (held[at] ?? NONE))))
  const replies = prompts.map((prompt, i) => replyOf(prompt, picked(i)))
  const complete = replies.every((reply) => reply !== null)
  const late = expired(question, now)
  const sending = entry?.phase === 'sending'
  // An answer the server refused as stale, or one kept that its question outgrew before it went.
  const dropped = entry?.phase === 'dropped' && entry.version !== question.version
  const failed = entry?.phase === 'failed' && entry.version === question.version ? entry.error : undefined

  const submit = async (event?: FormEvent) => {
    event?.preventDefault()
    if (!complete || sending) return
    setChanged(false)
    const outcome = await send(question, replies as Reply[])
    if (outcome === 'stale') setChanged(true)
  }
  const onKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      void submit()
    }
  }
  // The same answer again is a retry, which sends the same mutation id (answers.tsx).
  const retry = failed !== undefined && JSON.stringify(entry?.replies) === JSON.stringify(replies)
  const label = retry ? 'Try again' : late ? 'Answer anyway' : several ? 'Send answers' : 'Send answer'
  const hint = !complete ? (several ? 'Answer each part to send.' : 'Pick or write an answer to send.')
    : offline ? 'Offline: your answer waits on this phone while Poulpe stays open, and goes once it is back online.'
      : `Sends version ${question.version}.`
  return (
    <Screen
      top={<TopBar title="Question" />}
      foot={
        <>
          <p className="form-hint">{hint}</p>
          <button type="submit" form={formId} className="button button-primary m-target" disabled={!complete || sending}>
            {sending ? 'Sending…' : label}
          </button>
        </>
      }
    >
      <Context question={question} />
      {(changed || dropped) && (
        <Note tone="question-changed">
          {dropped ? 'Your answer was not sent: this question changed before it went. Read it again.' : 'This question changed while you were answering: read it again. What you had picked was for the earlier version and was cleared.'}
        </Note>
      )}
      {question.reopened && <Note tone="question-error">{question.reopened}</Note>}
      {question.expiry && late && <Note tone="question-expired">{expiredText(question.expiry, now)}. An answer still reaches it.</Note>}
      <form id={formId} onSubmit={(event) => void submit(event)} onKeyDown={onKeyDown} className="request-card question-card m-form" aria-labelledby={titleId}>
        {several ? (
          <h2 className="request-title" id={titleId}>The session asks</h2>
        ) : (
          <div role="heading" aria-level={2} className="request-title" id={titleId}>{prompts[0] && promptTitle(prompts[0])}</div>
        )}
        {prompts.map((prompt, i) => (
          <div key={`${question.version}-${i}`} className="m-prompt">
            {prompt.choices.length > 0
              ? <Choices prompt={prompt} several={several} pick={picked(i)} disabled={sending} onChange={(next) => change(i, next)} />
              : <Words prompt={prompt} several={several} pick={picked(i)} disabled={sending} onChange={(next) => change(i, next)} />}
          </div>
        ))}
      </form>
      {failed && <Note tone="question-error" role="alert">{several ? 'Could not send the answers' : 'Could not send the answer'}: {failed}. Trying again sends the same answer once.</Note>}
    </Screen>
  )
}
